/**
 * SCORM Error Handling Utilities
 * Maps backend errors to SCORM 1.2 error codes and messages
 */

import { ScormErrorCodes } from "./types";

export class ScormError extends Error {
  public readonly code: ScormErrorCodes;
  public readonly scormMessage: string;

  constructor(code: ScormErrorCodes, message: string, scormMessage?: string) {
    super(message);
    this.name = "ScormError";
    this.code = code;
    this.scormMessage = scormMessage ?? this.getScormErrorString(code);
  }

  private getScormErrorString(errorCode: ScormErrorCodes): string {
    switch (errorCode) {
      case ScormErrorCodes.NO_ERROR:
        return "No error";
      case ScormErrorCodes.GENERAL_EXCEPTION:
        return "General exception";
      case ScormErrorCodes.GENERAL_INITIALIZATION_FAILURE:
        return "General initialization failure";
      case ScormErrorCodes.ALREADY_INITIALIZED:
        return "Already initialized";
      case ScormErrorCodes.CONTENT_INSTANCE_TERMINATED:
        return "Content instance terminated";
      case ScormErrorCodes.GENERAL_TERMINATION_FAILURE:
        return "General termination failure";
      case ScormErrorCodes.TERMINATION_BEFORE_INIT:
        return "Termination before initialization";
      case ScormErrorCodes.TERMINATION_AFTER_TERM:
        return "Termination after termination";
      case ScormErrorCodes.RETRIEVE_DATA_BEFORE_INIT:
        return "Retrieve data before initialization";
      case ScormErrorCodes.RETRIEVE_DATA_AFTER_TERM:
        return "Retrieve data after termination";
      case ScormErrorCodes.STORE_DATA_BEFORE_INIT:
        return "Store data before initialization";
      case ScormErrorCodes.STORE_DATA_AFTER_TERM:
        return "Store data after termination";
      case ScormErrorCodes.COMMIT_BEFORE_INIT:
        return "Commit before initialization";
      case ScormErrorCodes.COMMIT_AFTER_TERM:
        return "Commit after termination";
      case ScormErrorCodes.GENERAL_ARGUMENT_ERROR:
        return "General argument error";
      case ScormErrorCodes.GENERAL_GET_FAILURE:
        return "General get failure";
      case ScormErrorCodes.GENERAL_SET_FAILURE:
        return "General set failure";
      case ScormErrorCodes.GENERAL_COMMIT_FAILURE:
        return "General commit failure";
      case ScormErrorCodes.UNDEFINED_DATA_MODEL:
        return "Undefined data model element";
      case ScormErrorCodes.UNIMPLEMENTED_ELEMENT:
        return "Unimplemented data model element";
      case ScormErrorCodes.VALUE_NOT_INITIALIZED:
        return "Value not initialized";
      case ScormErrorCodes.READ_ONLY_ELEMENT:
        return "Data model element is read only";
      case ScormErrorCodes.WRITE_ONLY_ELEMENT:
        return "Data model element is write only";
      case ScormErrorCodes.TYPE_MISMATCH:
        return "Data model element type mismatch";
      default:
        return "Unknown error";
    }
  }
}

/**
 * Maps HTTP error responses to SCORM error codes
 */
export function mapHttpErrorToScormError(error: any): ScormError {
  const status = error?.response?.status;
  const message = error?.response?.data?.message || error.message;

  switch (status) {
    case 400:
      return new ScormError(ScormErrorCodes.GENERAL_ARGUMENT_ERROR, message);
    case 401:
    case 403:
      return new ScormError(
        ScormErrorCodes.GENERAL_EXCEPTION,
        "Authentication failed"
      );
    case 404:
      return new ScormError(
        ScormErrorCodes.UNDEFINED_DATA_MODEL,
        "Resource not found"
      );
    case 409:
      return new ScormError(
        ScormErrorCodes.ALREADY_INITIALIZED,
        "Conflict with existing state"
      );
    case 422:
      return new ScormError(
        ScormErrorCodes.GENERAL_ARGUMENT_ERROR,
        "Validation failed"
      );
    case 500:
    case 502:
    case 503:
      return new ScormError(ScormErrorCodes.GENERAL_EXCEPTION, "Server error");
    default:
      return new ScormError(
        ScormErrorCodes.GENERAL_EXCEPTION,
        message || "Unknown error"
      );
  }
}

/**
 * Handles network failures during SCORM operations
 */
export function handleNetworkError(error: any): ScormError {
  if (error.code === "ECONNABORTED" || error.code === "NETWORK_ERROR") {
    return new ScormError(
      ScormErrorCodes.GENERAL_EXCEPTION,
      "Network timeout or connection failed"
    );
  }

  if (!navigator.onLine) {
    return new ScormError(
      ScormErrorCodes.GENERAL_EXCEPTION,
      "No internet connection"
    );
  }

  return mapHttpErrorToScormError(error);
}
